import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../shared/TailwindComponents/Button';
import { PiSpinnerGapLight } from 'react-icons/pi';

interface DeleteClientsConfirmationProps {
  /** How many clients are being deleted */
  clientCount: number;
  onConfirm: () => Promise<void> | void;
  onCancel: () => void;
}

const DeleteClientsConfirmation: React.FC<DeleteClientsConfirmationProps> = ({
  clientCount,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);

  const isPlural = clientCount > 1;

  const handleConfirm = async () => {
    setIsDeleting(true);
    try {
      await onConfirm();
    } catch {
      setIsDeleting(false);
    }
  };

  if (isDeleting) {
    return (
      <div className="flex flex-col items-center justify-center py-10 px-4 gap-4">
        <div className="relative w-12 h-12">
          <PiSpinnerGapLight className="w-12 h-12 text-red-500 animate-spin" />
        </div>
        <div className="text-center space-y-1.5">
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            {isPlural
              ? t(`Deleting ${clientCount} clients...`)
              : t('Deleting client...')}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('Please wait, this may take a moment.')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="py-1 px-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {isPlural
            ? t(
                'This will permanently delete {{count}} selected clients, including their documents and matter associations. This action cannot be undone.',
                { count: clientCount }
              )
            : t(
                'This will permanently delete the selected client, including their documents and matter associations. This action cannot be undone.'
              )}
        </p>
      </div>

      {/* Action buttons */}
      <div className="flex justify-end space-x-4 pt-6 mt-6 border-t border-gray-300 px-4">
        <Button variant="neutral" size="small" onClick={onCancel} disabled={isDeleting} className="w-24">
          {t('Cancel')}
        </Button>
        <Button
          variant="destructive"
          size="small"
          onClick={handleConfirm}
          disabled={isDeleting}
          className="w-24"
        >
          {t('Delete')}
        </Button>
      </div>
    </div>
  );
};

export default DeleteClientsConfirmation;
